import React, { useState } from 'react'
import ProgressChecklist from './ProgressChecklist'
import { useConversations } from '../chat/ConversationContext'
import Tooltip from '../shared/Tooltip'
import { format, parseISO } from 'date-fns'
import { destroy } from '@rails/request.js'

// Safe hook that provides fallback when ConversationProvider is not available
const useSafeConversations = () => {
  try {
    return useConversations()
  } catch {
    // Return fallback object when no ConversationProvider is available
    return {
      getUnreadCountForLawFirm: () => 0,
    }
  }
}

const DocumentCard = ({ document }) => {
  const { getUnreadCountForLawFirm } = useSafeConversations()
  const [isArchiving, setIsArchiving] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const handleMessageCollector = () => {
    if (!document.law_firm || !document.law_firm.in_database) return

    // Dispatch a custom event to trigger the chat widget to open
    const event = new window.CustomEvent('openChatWithUser', {
      detail: {
        recipientId: document.law_firm.id,
        documentId: document.id,
        recipientName: document.law_firm.name,
        recipientStatus: 'Collector',
        recipientType: 'law_firm',
      },
    })

    window.dispatchEvent(event)
  }

  const redirectWithMessage = ({ success, message }) => {
    const url = new URL(window.location)
    url.searchParams.set('success', success.toString())
    url.searchParams.set('message', message)
    window.location.href = url.toString()
  }

  const handleArchiveDocument = async () => {
    const defaultErrorMessage =
      'Unable to archive document. Please try again or contact support if the problem persists.'

    setIsArchiving(true)
    setShowConfirmDialog(false)

    try {
      const response = await destroy(`/my_dashboard/document/${document.id}/archive`, {
        responseKind: 'json',
      })

      if (!response.ok) {
        redirectWithMessage({ success: false, message: defaultErrorMessage })
        return
      }

      const data = await response.json

      if (!data.success) {
        redirectWithMessage({ success: false, message: defaultErrorMessage })
        return
      }

      redirectWithMessage({ success: true, message: data.message })
    } catch {
      redirectWithMessage({ success: false, message: defaultErrorMessage })
    } finally {
      setIsArchiving(false)
    }
  }

  const renderMessageButton = () => {
    // Only show for SoloSettle documents
    if (document.title !== 'SoloSettle') return null

    // Hide button if no law firm is assigned
    if (!document.law_firm) {
      return null
    }

    // Hide button if law firm is not in database
    if (!document.law_firm.in_database) {
      return null
    }

    // Law firm is assigned and in database - show active button
    const unreadCount = getUnreadCountForLawFirm(document.law_firm.id)

    return (
      <button
        className='btn btn-outline-primary d-flex justify-content-center align-items-center py-3 mt-4 text-wrap'
        onClick={handleMessageCollector}
        style={{ position: 'relative' }}
      >
        <span>Message Collector</span>
        <i className='fe fe-message-square ms-2'></i>
        {unreadCount > 0 && (
          <span
            className='position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger'
            style={{ fontSize: '0.75rem' }}
          >
            {unreadCount}
          </span>
        )}
      </button>
    )
  }

  return (
    <div id={`document-card-${document.id}`} className='d-flex flex-column gap-4'>
      <div className='d-flex justify-content-between align-items-start'>
        <div className='flex-grow-1'>
          <a
            href={
              document.is_completed
                ? `/my_dashboard/download?id=${document.id}`
                : document.resume_url
            }
            className='text-decoration-none mb-2'
            data-remote='false'
            data-turbolinks='false'
          >
            <h3 className='text-primary mb-0'>
              {document?.sectioned_steps
                ?.flatMap(section => section.steps)
                ?.find(step => step.wizard_step_name === 'generated_document_type')?.value ||
                document.title}
            </h3>
          </a>
          <p className='fs-6 text-body mb-0'>
            Started on {format(parseISO(document.created_at), 'MMMM d, yyyy')}
          </p>
        </div>
        <div className='d-flex align-items-center'>
          <Tooltip
            text='Archiving will remove this document from your dashboard.'
            width='240px'
            position='top'
          >
            <button
              className='btn btn-link p-1 text-muted'
              onClick={() => setShowConfirmDialog(true)}
              disabled={isArchiving}
              style={{ lineHeight: 1 }}
              aria-label='Archive document'
            >
              <i className='fe fe-archive fs-5'></i>
            </button>
          </Tooltip>
        </div>
      </div>

      {!document.is_completed && document.resume_url && document.title == 'SoloSettle' && (
        <a
          href={document.resume_url}
          className='btn btn-primary d-flex justify-content-center align-items-center py-3 text-wrap'
          data-remote='false'
          data-turbolinks='false'
        >
          <span>Continue Settlement Process</span>
          <i className='fe fe-arrow-right ms-2'></i>
        </a>
      )}

      <div className='d-flex flex-column'>
        <h3 className='mb-2'>Status</h3>
        <div className='d-flex align-items-center'>
          <a
            href={
              document.is_completed
                ? `/my_dashboard/download?id=${document.id}`
                : document.resume_url
            }
            className='text-decoration-none'
            data-remote='false'
            data-turbolinks='false'
          >
            <span
              className={`badge rounded-pill border border-${document.status_color} text-${document.status_color} bg-transparent fs-4`}
              style={{
                padding: '0.15rem 0.5rem',
                lineHeight: 1.5,
              }}
            >
              {document.status}
            </span>
          </a>
          {document.status_definition && (
            <Tooltip
              text={document.status_definition}
              width='320px'
              position='top'
              className='mb-1'
            />
          )}
        </div>
      </div>
      <div className='d-flex flex-column'>
        <h3 className='mb-2'>Progress Checklist</h3>
        <div className='accordion inverted-accordion' id={`accordion-${document.id}`}>
          {document.sectioned_steps?.map((section, sectionIndex) => (
            <ProgressChecklist
              key={sectionIndex}
              document={document}
              section={section}
              sectionIndex={sectionIndex}
            />
          ))}
        </div>

        {/* Message Collector button positioned after Progress Checklist */}
        {renderMessageButton()}

        {document.downloads && document.downloads.length > 0 && (
          <div className='d-flex flex-column mt-4'>
            <h3 className='mb-2'>Files</h3>
            <div className='d-flex flex-column gap-2'>
              {document.downloads.map((download, index) => (
                <a
                  key={index}
                  href={download.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-primary d-inline-flex align-items-center'
                >
                  <i className='fe fe-download me-1'></i>
                  <span className='text-decoration-underline'>{download.title}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div className='modal d-block' style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className='modal-dialog modal-dialog-centered'>
              <div className='modal-content'>
                <div className='modal-header pb-0'>
                  <h2 className='modal-title'>Confirm Archive</h2>
                  <button
                    type='button'
                    className='btn-close'
                    style={{ transform: 'scale(1.5)' }}
                    onClick={() => setShowConfirmDialog(false)}
                  ></button>
                </div>
                <div className='modal-body py-2'>
                  <p className='mb-0'>This action cannot be easily undone.</p>
                </div>
                <div className='modal-footer pt-0'>
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={handleArchiveDocument}
                    disabled={isArchiving}
                  >
                    {isArchiving ? 'Archiving...' : 'Archive Document'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DocumentCard
